import React, { useState, useEffect, useRef } from 'react'
import { Form, Button, Collapse, Checkbox } from 'antd'
import { PlusOutlined, MinusOutlined } from '@ant-design/icons'
import MultiSelector from './MultiSelector'
import PeriodRangePicker from './PeriodRangePicker'
import { apiFetch } from "../utils/api"
import { useDateRange } from '../hooks/useDateRange'
import dayjs from 'dayjs'

const { Panel } = Collapse

export default function WarningsFilter({
  devices,
  initialDevices = [],
  initialRange = null,
  onApplyFilters,
  disabledDevices = false,
  elements,
  initialElements = [],
  disabledElements = false,
  defaultCollapsed = false,
  statusFilter,
  setStatusFilter
}) {
  const [selectedDevices, setSelectedDevices] = useState(initialDevices)
  const [selectedElements, setSelectedElements] = useState(initialElements)
  const [activeKey, setActiveKey] = useState(defaultCollapsed ? [] : ['filters'])
  const [loading, setLoading] = useState(false)
  const [filteredElements, setFilteredElements] = useState([])

  const periodPickerRef = useRef()
  const [customRange, setCustomRange] = useState({
    from: dayjs().subtract(24, 'hour'),
    to: dayjs(),
  });

  const {
    showCustom,
    selectedLabel,
    setPreset,
    setShowCustom,
    selectedPreset,
    presetDefs,
  } = useDateRange(setCustomRange)

  // Inicialización
  useEffect(() => setSelectedDevices(initialDevices), [initialDevices])
  useEffect(() => setSelectedElements(initialElements), [initialElements])

  // Carga dinámica de elementos según dispositivos seleccionados
  useEffect(() => {
    setFilteredElements([])
    if (selectedDevices.length === 0) return

    setLoading(true)
    apiFetch('/api/elements/by-devices', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: selectedDevices }),
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then(data => setFilteredElements(data))
      .catch(err => {
        console.error('Failed to load elements', err)
        setFilteredElements([])
      })
      .finally(() => setLoading(false))
  }, [selectedDevices])

  const normalizeStatusFilter = (list) => {
    if (!list || list.length === 0) return [];

    if (list.includes("ALL")) {
      return ["OPEN", "CLOSED", "CONFIRMED"];
    }

    let result = [...list];

    if (list.includes("UNCONFIRMED")) {
      result = [...result, "OPEN", "CLOSED"];
    }

    return [...new Set(result)];
  };

  const handleApply = () => {
    if (!periodPickerRef.current) return;

    let range = periodPickerRef.current.getRange();

    // ✅ Recalcular presets relativos al aplicar
    if (!periodPickerRef.current.isCustom) {
      const preset = presetDefs.find(p => p.key === selectedPreset);
      if (preset) {
        const now = dayjs();
        range = {
          from: now.subtract(preset.n, preset.unit),
          to: now,
        };
      }
    }

    onApplyFilters(
      selectedDevices,
      selectedElements,
      range,
      normalizeStatusFilter(statusFilter)
    );
  };

  // UI: icono del collapse
  const isOpen = Array.isArray(activeKey) ? activeKey.includes('filters') : activeKey === 'filters'
  const headerText = isOpen ? 'Hide filters' : 'Show filters'
  const headerIcon = isOpen
    ? <MinusOutlined style={{ fontSize: 13, lineHeight: 1, verticalAlign: 'middle' }} />
    : <PlusOutlined style={{ fontSize: 13, lineHeight: 1, verticalAlign: 'middle' }} />

  return (
    <Collapse
      activeKey={activeKey}
      onChange={setActiveKey}
      style={{ marginBottom: 10 }}
      expandIcon={() => null}
      items={[
        {
          key: 'filters',
          label: (
            <div
              style={{
                minHeight: 24,
                padding: '2px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              {headerIcon}
              <span>{headerText}</span>
            </div>
          ),
          children: (
            <Form layout="vertical">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                {/* Devices */}
                <Form.Item label="Devices" style={{ minWidth: 250, marginBottom: '5px' }}>
                  <MultiSelector
                    items={devices}
                    value={selectedDevices}
                    onChange={setSelectedDevices}
                    placeholder="Select devices"
                    disabled={disabledDevices}
                  />
                </Form.Item>

                {/* Elements */}
                <Form.Item label="Elements" style={{ minWidth: 250, marginBottom: '5px' }}>
                  <MultiSelector
                    items={filteredElements}
                    value={selectedElements}
                    onChange={setSelectedElements}
                    placeholder="Select elements"
                    disabled={filteredElements.length === 0 || disabledElements}
                    multiple
                  />
                </Form.Item>

                {/* Period */}
                <Form.Item label="Period" style={{ minWidth: 290, marginBottom: '5px' }}>
                  <PeriodRangePicker
                    ref={periodPickerRef}
                    value={customRange}
                    onChange={setCustomRange}
                    showCustom={showCustom}
                    setPreset={setPreset}
                    setShowCustom={setShowCustom}
                    selectedPreset={selectedPreset}
                    selectedLabel={selectedLabel}
                    presetDefs={presetDefs}
                  />
                </Form.Item>

                <Form.Item label="Status" style={{ minWidth: 300, marginBottom: '5px' }}>
                  <Checkbox.Group
                    value={statusFilter}
                    onChange={setStatusFilter}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '12px 24px'
                    }}
                  >
                    <Checkbox value="ALL">All</Checkbox>
                    <Checkbox value="OPEN">Open</Checkbox>
                    <Checkbox value="CLOSED">Closed</Checkbox>
                    <Checkbox value="CONFIRMED">Confirmed</Checkbox>
                    <Checkbox value="UNCONFIRMED">Unconfirmed</Checkbox>
                  </Checkbox.Group>
                </Form.Item>

                {/* Apply button */}
                <div
                  style={{
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'flex-end',
                    marginBottom: '5px',
                  }}
                >
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      type="primary"
                      onClick={handleApply}
                      disabled={disabledDevices || loading}
                    >
                      Apply Filters
                    </Button>
                  </Form.Item>
                </div>
              </div>
            </Form>
          ),
          style: { padding: 0 },
        },
      ]}
    />
  )
}
